/**
 * AppDialog — shared message + confirmation dialog for all 4 portals.
 *
 * Replaces every native `alert()` and `window.confirm()` with a
 * styled, accessible Radix-based dialog that matches the app's theme.
 *
 * Usage:
 *
 *   // 1. Wrap your app once (typically in App.js alongside AuthProvider)
 *   import { DialogProvider, useDialog } from '@bc/ui';
 *
 *   function App() {
 *     return (
 *       <DialogProvider>
 *         <Routes>...</Routes>
 *       </DialogProvider>
 *     );
 *   }
 *
 *   // 2. Use anywhere via the hook
 *   function MyComponent() {
 *     const { alert, confirm } = useDialog();
 *
 *     const handleDelete = async () => {
 *       const yes = await confirm(
 *         'Delete this item?',
 *         'This action cannot be undone.'
 *       );
 *       if (yes) { ... }
 *     };
 *
 *     const handleSuccess = () => {
 *       alert('Done!', 'Your request was submitted successfully.');
 *     };
 *   }
 *
 * The dialog is portal-mounted, so it works from any depth in the
 * component tree without prop drilling.
 */

import React, {
  createContext,
  useCallback,
  useContext,
  useRef,
  useState,
} from "react";

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------

const DialogContext = createContext(null);

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

export function DialogProvider({ children }) {
  const [dialog, setDialog] = useState(null);
  const resolveRef = useRef(null);

  const close = useCallback((result) => {
    setDialog(null);
    if (resolveRef.current) {
      resolveRef.current(result);
      resolveRef.current = null;
    }
  }, []);

  const show = useCallback(
    ({ title, message, type }) =>
      new Promise((resolve) => {
        resolveRef.current = resolve;
        setDialog({ title, message, type });
      }),
    [],
  );

  const alert = useCallback(
    (title, message) => show({ title, message, type: "alert" }),
    [show],
  );

  const confirm = useCallback(
    (title, message) => show({ title, message, type: "confirm" }),
    [show],
  );

  return (
    <DialogContext.Provider value={{ alert, confirm }}>
      {children}
      {dialog && (
        <DialogOverlay>
          <DialogPanel>
            {dialog.title && <DialogTitle>{dialog.title}</DialogTitle>}
            {dialog.message && (
              <DialogMessage>{dialog.message}</DialogMessage>
            )}
            <DialogActions>
              {dialog.type === "confirm" && (
                <DialogButton variant="cancel" onClick={() => close(false)}>
                  Cancel
                </DialogButton>
              )}
              <DialogButton
                variant="primary"
                onClick={() => close(true)}
                autoFocus
              >
                {dialog.type === "confirm" ? "Confirm" : "OK"}
              </DialogButton>
            </DialogActions>
          </DialogPanel>
        </DialogOverlay>
      )}
    </DialogContext.Provider>
  );
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useDialog() {
  const ctx = useContext(DialogContext);
  if (!ctx) {
    throw new Error("useDialog must be used within <DialogProvider>");
  }
  return ctx;
}

// ---------------------------------------------------------------------------
// Styled sub-components (inline — no external CSS dependency)
// ---------------------------------------------------------------------------

function DialogOverlay({ children }) {
  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 9999,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: "rgba(0, 0, 0, 0.5)",
        backdropFilter: "blur(2px)",
        padding: "1rem",
      }}
    >
      {children}
    </div>
  );
}

function DialogPanel({ children }) {
  return (
    <div
      role="dialog"
      aria-modal="true"
      style={{
        backgroundColor: "var(--dialog-bg, #ffffff)",
        color: "var(--dialog-fg, #111111)",
        border: "1px solid var(--dialog-border, #e5e7eb)",
        borderRadius: "var(--radius, 0px)",
        maxWidth: "28rem",
        width: "100%",
        padding: "1.5rem",
        boxShadow: "0 20px 60px rgba(0,0,0,0.15)",
        animation: "bc-dialog-in 0.15s ease-out",
      }}
    >
      <style>{`
        @keyframes bc-dialog-in {
          from { opacity: 0; transform: scale(0.95) translateY(10px); }
          to { opacity: 1; transform: scale(1) translateY(0); }
        }
      `}</style>
      {children}
    </div>
  );
}

function DialogTitle({ children }) {
  return (
    <h2
      style={{
        margin: "0 0 0.5rem 0",
        fontSize: "1.125rem",
        fontWeight: 700,
        lineHeight: 1.3,
      }}
    >
      {children}
    </h2>
  );
}

function DialogMessage({ children }) {
  return (
    <p
      style={{
        margin: "0 0 1.25rem 0",
        fontSize: "0.875rem",
        lineHeight: 1.5,
        opacity: 0.8,
        whiteSpace: "pre-line",
      }}
    >
      {children}
    </p>
  );
}

function DialogActions({ children }) {
  return (
    <div
      style={{
        display: "flex",
        justifyContent: "flex-end",
        gap: "0.5rem",
      }}
    >
      {children}
    </div>
  );
}

function DialogButton({ variant, children, onClick, autoFocus }) {
  const isPrimary = variant === "primary";
  return (
    <button
      type="button"
      onClick={onClick}
      autoFocus={autoFocus}
      style={{
        padding: "0.625rem 1.25rem",
        fontSize: "0.8125rem",
        fontWeight: 600,
        minHeight: "44px",
        border: isPrimary ? "none" : "1px solid var(--dialog-border, #e5e7eb)",
        borderRadius: "var(--radius, 0px)",
        cursor: "pointer",
        textTransform: "uppercase",
        letterSpacing: "0.05em",
        backgroundColor: isPrimary
          ? "var(--dialog-primary, hsl(220 100% 40%))"
          : "transparent",
        color: isPrimary
          ? "var(--dialog-primary-fg, #ffffff)"
          : "var(--dialog-fg, #111111)",
        transition: "opacity 0.15s",
      }}
      onMouseEnter={(e) => (e.target.style.opacity = "0.85")}
      onMouseLeave={(e) => (e.target.style.opacity = "1")}
    >
      {children}
    </button>
  );
}
